// src/features/records/services/interactions/reactions.js

import { db, functions } from '../../../../firebase';
import { collection, doc, getCountFromServer, getDoc, getDocs, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { requireUser } from '../utils/auth';

let toggleRecordReactionFn = httpsCallable(functions, 'toggleRecordReaction');

function getReactorsRef(recordId, reactionKey) {
  return collection(db, 'records', recordId, 'reactions', reactionKey, 'reactors');
}

/**
 * Toggle the canonical like reaction for a record via the `toggleRecordReaction` callable.
 */
export async function toggleRecordReaction(recordId, reactionKey = 'e:2764') {
  requireUser();

  if (!recordId || typeof recordId !== 'string') {
    throw new Error('recordId is required to toggle reactions');
  }

  const payload = {
    recordId,
    reactionKey
  };

  try {
    if (!toggleRecordReactionFn) {
      toggleRecordReactionFn = httpsCallable(functions, 'toggleRecordReaction');
    }

    const response = await toggleRecordReactionFn(payload);
    const data = response?.data ?? {};
    const normalisedCount = Number(data.count);

    return {
      recordId: typeof data.recordId === 'string' ? data.recordId : recordId,
      reactionKey: typeof data.reactionKey === 'string' ? data.reactionKey : reactionKey,
      liked: Boolean(data.liked),
      count: Number.isFinite(normalisedCount) ? normalisedCount : 0
    };
  } catch (error) {
    console.error('Error toggling record reaction:', error);
    throw error;
  }
}

/**
 * Subscribe to the current user's reaction membership.
 */
export function subscribeToReactionMembership({ recordId, reactionKey, userId }, onChange, onError) {
  if (!recordId || !reactionKey || !userId) {
    return () => {};
  }

  const reactorDoc = doc(db, 'records', recordId, 'reactions', reactionKey, 'reactors', userId);

  return onSnapshot(
    reactorDoc,
    (snapshot) => {
      if (typeof onChange === 'function') {
        onChange(snapshot.exists());
      }
    },
    (err) => {
      if (typeof onError === 'function') {
        onError(err);
      }
    }
  );
}

/**
 * Fetch aggregate reaction count.
 */
export async function getReactionAggregateCount(recordId, reactionKey) {
  const reactorsRef = getReactorsRef(recordId, reactionKey);
  const aggregateSnapshot = await getCountFromServer(reactorsRef);
  return aggregateSnapshot?.data()?.count;
}

/**
 * Fetch reaction node snapshot.
 */
export async function getReactionNodeSnapshot(recordId, reactionKey) {
  const reactionNodeRef = doc(db, 'records', recordId, 'reactions', reactionKey);
  return getDoc(reactionNodeRef);
}

/**
 * Enumerate reactors and return count.
 */
export async function getReactionEnumerationCount(recordId, reactionKey) {
  const reactorsRef = getReactorsRef(recordId, reactionKey);
  const snapshot = await getDocs(reactorsRef);
  return snapshot?.size ?? 0;
}
